import { useState } from "react";
import { es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

/** `2026-08-03` → `03/08/2026`. Vacío o mal formado → `""`. */
function formatearFechaISO(iso: string): string {
  const [anio, mes, dia] = iso.split("-");
  return anio && mes && dia ? `${dia}/${mes}/${anio}` : "";
}

/**
 * `Date` → `2026-08-03` usando los componentes LOCALES.
 *
 * `toISOString().slice(0, 10)` convierte a UTC primero: en Chile (UTC-4) un
 * `Date` a medianoche vuelve como el día ANTERIOR. El valor que manejamos es
 * una fecha civil, sin hora ni huso, así que se arma a mano.
 */
function aISO(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, "0");
  const dia = String(fecha.getDate()).padStart(2, "0");
  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** `2026-08-03` → `Date` a medianoche local, sin corrimiento de huso. */
function desdeISO(iso: string): Date | undefined {
  if (!formatearFechaISO(iso)) return undefined;
  const [anio, mes, dia] = iso.split("-").map(Number);
  return new Date(anio, mes - 1, dia);
}

export interface CampoFechaProps {
  id?: string;
  /** Fecha civil `YYYY-MM-DD`. `""` = sin elegir. */
  value: string;
  onChange: (iso: string) => void;
  /** Fecha civil mínima seleccionable, inclusive (`YYYY-MM-DD`). */
  min?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
}

/**
 * Campo de fecha del Design System — reemplaza a `<input type="date">`.
 *
 * MOTIVO: el formato que dibuja el input nativo lo decide el idioma
 * configurado en el navegador, y no hay atributo que lo fuerce. El mismo 28 de
 * julio se ve `07/28/2026` en un Chrome en inglés, `28 - 07 - 2026` en Firefox
 * y distinto en Safari. Para un dato que el usuario tiene que leer y confirmar
 * —cuándo sale una capacitación, cuándo vence un documento— eso no es
 * aceptable: la misma pantalla dice cosas distintas según quién la abra.
 *
 * Acá el trigger es un botón que muestra SIEMPRE dd/mm/aaaa y el calendario es
 * `react-day-picker` en español, así que el formato y los nombres de mes son
 * nuestros en cualquier navegador. El valor que entra y sale sigue siendo
 * `YYYY-MM-DD`, igual que el input nativo, para no tocar a los consumidores ni
 * el contrato con la API.
 */
export function CampoFecha({
  id,
  value,
  onChange,
  min,
  disabled = false,
  placeholder = "dd/mm/aaaa",
  className,
  "aria-label": ariaLabel,
}: CampoFechaProps) {
  const [abierto, setAbierto] = useState(false);
  const seleccionada = desdeISO(value);
  const minima = min ? desdeISO(min) : undefined;
  const etiqueta = formatearFechaISO(value);

  return (
    <Popover open={abierto} onOpenChange={setAbierto}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          // Mismas clases que `ui/input` para que no se distinga de un campo
          // normal: si se separan, este control queda fuera del sistema.
          className={cn(
            "border-input bg-background flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-lg border px-[11px] text-[13px] transition-colors outline-none",
            "hover:border-border-strong",
            "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            "focus-visible:border-primary focus-visible:ring-ring focus-visible:ring-2",
            className
          )}
        >
          <span className={cn("fc-num", !etiqueta && "text-soft")}>
            {etiqueta || placeholder}
          </span>
          <Icon name="calendar_today" size={16} className="text-icon-soft shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          locale={es}
          selected={seleccionada}
          defaultMonth={seleccionada ?? minima}
          disabled={minima ? { before: minima } : undefined}
          onSelect={(fecha) => {
            if (!fecha) return;
            onChange(aISO(fecha));
            setAbierto(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
